import { IOrderOtherSectionsButtonsLogic } from "../../components/order_entry/OrderOtherSectionsButtons/IOrderOtherSectionsButtonsLogic";
import { useContext, useMemo } from "react";
import OrderStatesEnum from "../../models/OrderStatesEnum";
import OrderEntryService from "../../services/OrderEntryService";
import IOrderUI from "../../models/IOrderUI";
import SupplierContext from "../../contexts/SupplierContext";
import UserContext from "../../contexts/UserContext";
import UserTypesEnum from "../../models/UserTypesEnum";
import PartialShipBehaviorEnum from "../../models/PartialShipBehaviorEnum";

export const useOrderOtherSectionsButtonsLogic = (props: { order: IOrderUI }): IOrderOtherSectionsButtonsLogic => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);

    const logic = useMemo(() => {
        const defaultLogic: IOrderOtherSectionsButtonsLogic = {
            showPickPackButtons: false,
            showManualShipBtns: false,
            showReleaseForShipmentBtn: false,
            showLocalDeliveryBtn: false,
            showLocalPickupBtn: false,
            showBackorderBtn: false,
            showPurchaseOrderBtn: false,
            showRmaBtn: false,
            hideQuoteOwButtons: false,
            showDownloadPdfButton: false,
            showFulfillBackorderBtn: false,
            showDeallocateBtn: false,
            showDispositionBtn: false,
        };

        const { currentStep, nextStep } = OrderEntryService.getCurrentAndNextOrderSteps(props.order, supplierModel);

        let showPickPackButtons = false;
        let showManualShipBtns = false;
        let showReleaseForShipmentBtn = false;
        let showLocalPickupBtn = false;
        let showLocalDeliveryBtn = false;
        let showDownloadPdfButton = false;
        let hideQuoteOwButtons = true;
        let showFulfillBackorderBtn = false;

        if (supplierModel.show_download_pdf) {
            showDownloadPdfButton = true;
        }

        if (!supplierModel.Supplier_Integration.qbo_hide_quote_ow_btns && props.order.is_quote) {
            hideQuoteOwButtons = false;
        }

        if (userModel.isSupplierUser) {
            let isVisible = false;

            if (
                userModel.supplierUserToken?.User_Type?.id === UserTypesEnum.ADMIN ||
                userModel.supplierUserToken?.User_Type?.id === UserTypesEnum.SHIP_MGMT ||
                userModel.supplierUserToken?.User_Type?.id === UserTypesEnum.ORDER_MGMT ||
                userModel.supplierUserToken?.User_Type?.id === UserTypesEnum.INVENTORY_MGMT
            ) {
                if (nextStep?.order_state_id === OrderStatesEnum.SHIPPED && !nextStep.autoCreateShipment) {
                    isVisible = true;
                }

                if (currentStep?.allowCreateFulfillment) {
                    isVisible = true;
                }

                for (const item of props.order.Order_Items || []) {
                    if (item.qty_pending && item.qty_pending > 0) {
                        showFulfillBackorderBtn = true;
                    }
                }
            }

            showManualShipBtns = isVisible;

            // Release for Shipment replaces shipment creation as the fulfillment
            // commit for release-model suppliers; shipments remain available as
            // informational records.
            if (
                isVisible &&
                currentStep?.allowCreateFulfillment &&
                supplierModel.partial_ship_behavior === PartialShipBehaviorEnum.RELEASE_FOR_SHIPMENT &&
                !props.order.is_quote &&
                !props.order.is_template &&
                !props.order.is_cancelled &&
                !props.order.is_local_pickup &&
                !props.order.is_local_delivery
            ) {
                showReleaseForShipmentBtn = true;
            }
        }

        if (showManualShipBtns) {
            if (props.order.is_local_pickup) {
                showLocalPickupBtn = true;
                showLocalDeliveryBtn = false;
            }

            if (props.order.is_local_delivery) {
                showLocalDeliveryBtn = true;
                showLocalPickupBtn = false;
            }
        }

        const showBackorderBtn =
            (supplierModel.has_backorders &&
                !!nextStep &&
                !props.order.is_quote &&
                !props.order.is_template &&
                userModel.isSupplierUser &&
                props.order.Order_Items?.length > 0 &&
                userModel.supplierUserToken?.user_type_id === UserTypesEnum.ADMIN) ||
            false;

        let showPurchaseOrderBtn = false;

        if (
            supplierModel.has_purchase_orders &&
            props.order.order_state_id !== OrderStatesEnum.PRELIMINARY &&
            !!nextStep &&
            userModel.isSupplierUser &&
            ((supplierModel.has_create_po_from_orders && userModel.supplierUserToken?.user_type_id === UserTypesEnum.ADMIN) ||
                supplierModel.prompt_order_entry_create_po)
        ) {
            showPurchaseOrderBtn = true;
        }

        let showRmaBtn = false;

        if (
            supplierModel.has_rma &&
            props.order.order_state_id !== OrderStatesEnum.PRELIMINARY &&
            !props.order.is_quote &&
            !props.order.is_template &&
            userModel.isSupplierUser &&
            userModel.supplierUserToken?.user_type_id === UserTypesEnum.ADMIN
        ) {
            // if the order is in one of the end states, show the RMA button
            if (OrderEntryService.isOrderAtEndWorkflowStep(props.order, supplierModel)) {
                showRmaBtn = true;
            }
        }

        let showDeallocateBtn = false;

        if (
            !props.order.is_quote &&
            !props.order.is_template &&
            props.order.order_state_id !== OrderStatesEnum.PRELIMINARY &&
            !props.order.is_cancelled &&
            userModel.isSupplierUser &&
            (userModel.supplierUserToken?.user_type_id === UserTypesEnum.ADMIN ||
                userModel.supplierUserToken?.user_type_id === UserTypesEnum.INVENTORY_MGMT)
        ) {
            const hasReserved = (props.order.Order_Items || []).some((item) => (item.qty_reserved || 0) > 0);
            if (hasReserved) {
                showDeallocateBtn = true;
            }
        }

        // Pick Pack Buttons
        if (
            userModel.isSupplierUser &&
            !supplierModel.has_pick_list_management &&
            userModel.supplierUserToken?.User_Type?.id !== UserTypesEnum.SALES &&
            props.order.order_state_id !== OrderStatesEnum.PRELIMINARY
        ) {
            showPickPackButtons = true;
        }

        if (supplierModel.settings?.screens?.order_entry?.hide_pick_pack_buttons) {
            showPickPackButtons = false;
        }

        let showDispositionBtn = false;

        if (
            supplierModel.Supplier_Integration?.has_fastbound &&
            props.order.has_serial_items &&
            !props.order.is_quote &&
            !props.order.is_template &&
            !props.order.is_cancelled &&
            userModel.isSupplierUser &&
            userModel.supplierUserToken?.user_type_id === UserTypesEnum.ADMIN &&
            props.order.order_state_id !== OrderStatesEnum.PRELIMINARY
        ) {
            showDispositionBtn = true;
        }

        return {
            ...defaultLogic,
            showPickPackButtons,
            showManualShipBtns,
            showReleaseForShipmentBtn,
            showLocalPickupBtn,
            showLocalDeliveryBtn,
            showBackorderBtn,
            showPurchaseOrderBtn,
            showRmaBtn,
            showDownloadPdfButton,
            hideQuoteOwButtons,
            showFulfillBackorderBtn,
            showDeallocateBtn,
            showDispositionBtn,
        };
    }, [props.order]);

    return logic;
};
